// Structured content on assistant `text` blocks (tables and charts).
//
// The backend sends a table or chart as a regular `text` block whose `value`
// is a readable plain-text version of it, plus optional structured fields:
//   table: { caption, headers: [string], rows: [[string]] }
//   chart: { chartType: 'bar' | 'line' | 'pie', title, labels: [string],
//            datasets: [{ label, data: [number | null] }] }
// These readers return a normalized copy, or null when the field is missing
// or malformed; the block then renders from `value` exactly as before, so a
// bad field never rejects the message.
//
// SECURITY: only strings and finite numbers are kept. Table cells render
// through Vue's `{{ }}` auto-escape and chart text is drawn on a canvas, so
// nothing here can inject markup.

export const CHART_TYPES = ['bar', 'line', 'pie'];

export const STRUCTURED_LIMITS = {
  maxColumns: 12,
  maxRows: 200,
  maxSeries: 6,
  maxPoints: 100,
  maxTextLength: 200,
};

const isPlainObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toText = value => (typeof value === 'string' ? value.slice(0, STRUCTURED_LIMITS.maxTextLength) : '');

const isStringArray = value => Array.isArray(value) && value.every(item => typeof item === 'string');

export function tableOf(block) {
  const table = block && block.type === 'text' && block.table;
  if (!isPlainObject(table)) { return null; }
  const { headers = [], rows = [] } = table;
  if (!isStringArray(headers) || !Array.isArray(rows) || !rows.every(isStringArray)) { return null; }
  if (headers.length > STRUCTURED_LIMITS.maxColumns || rows.length > STRUCTURED_LIMITS.maxRows) { return null; }
  if (rows.some(row => row.length > STRUCTURED_LIMITS.maxColumns)) { return null; }
  const columns = Math.max(headers.length, ...rows.map(row => row.length));
  if (columns === 0) { return null; }
  const pad = cells => Array.from({ length: columns }, (_unused, i) => toText(cells[i]));
  return {
    caption: toText(table.caption),
    headers: headers.length ? pad(headers) : [],
    rows: rows.map(pad),
  };
}

const isChartNumber = value => value === null || (typeof value === 'number' && Number.isFinite(value));

export function chartOf(block) {
  const chart = block && block.type === 'text' && block.chart;
  if (!isPlainObject(chart) || !CHART_TYPES.includes(chart.chartType)) { return null; }
  const { labels, datasets } = chart;
  if (!isStringArray(labels) || labels.length === 0 || labels.length > STRUCTURED_LIMITS.maxPoints) { return null; }
  if (!Array.isArray(datasets) || datasets.length === 0 || datasets.length > STRUCTURED_LIMITS.maxSeries) { return null; }
  const valid = datasets.every(dataset => isPlainObject(dataset)
    && Array.isArray(dataset.data)
    && dataset.data.length === labels.length
    && dataset.data.every(isChartNumber));
  if (!valid || !datasets.some(dataset => dataset.data.some(value => value !== null))) { return null; }
  return {
    chartType: chart.chartType,
    title: toText(chart.title),
    labels: labels.map(toText),
    // A pie shows one series; any extra ones are dropped rather than overlaid.
    datasets: (chart.chartType === 'pie' ? datasets.slice(0, 1) : datasets)
      .map(dataset => ({ label: toText(dataset.label), data: [...dataset.data] })),
  };
}

// True when the block renders as a table or chart instead of its `value` text.
export function isStructuredBlock(block) {
  return Boolean(chartOf(block) || tableOf(block));
}
